import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

export type SmartBreadcrumbItem = {
  label: ReactNode;
  href?: string;
  isCurrent?: boolean;
  key?: string;
};

type BreadcrumbsBuilderProps = {
  items: SmartBreadcrumbItem[];
  maxVisible?: number;
  keepFirst?: number;
  keepLast?: number;
  className?: string;
  listClassName?: string;
  itemClassName?: string;
  currentClassName?: string;
  renderLink?: (item: SmartBreadcrumbItem, children: ReactNode) => ReactNode;
};

type CollapsedSegment =
  | { type: 'item'; item: SmartBreadcrumbItem; originalIndex: number }
  | { type: 'ellipsis' };

const DEFAULT_MAX_VISIBLE = 4;
const DEFAULT_KEEP_FIRST = 1;
const DEFAULT_KEEP_LAST = 1;

function collapseItems(
  items: SmartBreadcrumbItem[],
  maxVisible: number,
  keepFirst: number,
  keepLast: number,
): CollapsedSegment[] {
  if (items.length <= maxVisible) {
    return items.map((item, originalIndex) => ({
      type: 'item',
      item,
      originalIndex,
    }));
  }

  // Keep one slot reserved for the ellipsis when collapsing.
  const edgeBudget = Math.max(0, maxVisible - 1);
  const safeKeepFirst = Math.max(0, Math.min(keepFirst, edgeBudget));
  const safeKeepLast = Math.max(
    0,
    Math.min(keepLast, Math.max(0, edgeBudget - safeKeepFirst)),
  );

  const start = items.slice(0, safeKeepFirst);
  const endStartIndex = items.length - safeKeepLast;
  const end = items.slice(endStartIndex);

  if (start.length + end.length >= items.length) {
    return items.map((item, originalIndex) => ({
      type: 'item',
      item,
      originalIndex,
    }));
  }

  return [
    ...start.map((item, originalIndex) => ({
      type: 'item' as const,
      item,
      originalIndex,
    })),
    { type: 'ellipsis' as const },
    ...end.map((item, index) => ({
      type: 'item' as const,
      item,
      originalIndex: endStartIndex + index,
    })),
  ];
}

function clampPositiveInt(value: number, fallback: number): number {
  if (!Number.isFinite(value) || value < 0) {
    return fallback;
  }

  return Math.floor(value);
}

export function BreadcrumbsBuilder({
  items,
  maxVisible = DEFAULT_MAX_VISIBLE,
  keepFirst = DEFAULT_KEEP_FIRST,
  keepLast = DEFAULT_KEEP_LAST,
  className,
  listClassName,
  itemClassName,
  currentClassName,
  renderLink,
}: BreadcrumbsBuilderProps) {
  if (items.length === 0) {
    return null;
  }

  const safeMaxVisible = Math.max(
    1,
    clampPositiveInt(maxVisible, DEFAULT_MAX_VISIBLE),
  );
  const safeKeepFirst = clampPositiveInt(keepFirst, DEFAULT_KEEP_FIRST);
  const safeKeepLast = clampPositiveInt(keepLast, DEFAULT_KEEP_LAST);
  const collapsed = collapseItems(
    items,
    safeMaxVisible,
    safeKeepFirst,
    safeKeepLast,
  );
  const renderedItems = collapsed.filter((segment) => segment.type === 'item');
  const hasExplicitCurrent = renderedItems.some(
    (segment) => segment.item.isCurrent,
  );
  const fallbackCurrentIndex =
    renderedItems[renderedItems.length - 1]?.originalIndex ?? items.length - 1;

  const content = (
    <Breadcrumb className={className}>
      <BreadcrumbList className={listClassName}>
        {collapsed.map((segment, index) => (
          <Fragment
            key={
              segment.type === 'ellipsis'
                ? `ellipsis-${index}`
                : (segment.item.key ??
                  `${segment.originalIndex}-${String(segment.item.label)}`)
            }
          >
            {index > 0 ? <BreadcrumbSeparator /> : null}

            {segment.type === 'ellipsis' ? (
              <BreadcrumbItem className={itemClassName}>
                <BreadcrumbEllipsis />
              </BreadcrumbItem>
            ) : (
              <BreadcrumbItem className={itemClassName}>
                {(() => {
                  const isCurrent = hasExplicitCurrent
                    ? Boolean(segment.item.isCurrent)
                    : segment.originalIndex === fallbackCurrentIndex;

                  if (isCurrent) {
                    return (
                      <BreadcrumbPage className={currentClassName}>
                        {segment.item.label}
                      </BreadcrumbPage>
                    );
                  }

                  if (renderLink) {
                    return renderLink(segment.item, segment.item.label);
                  }

                  if (segment.item.href) {
                    return (
                      <BreadcrumbLink asChild>
                        <Link to={segment.item.href}>{segment.item.label}</Link>
                      </BreadcrumbLink>
                    );
                  }

                  return (
                    <span
                      className={cn(
                        'tw:text-muted-foreground',
                        currentClassName,
                      )}
                    >
                      {segment.item.label}
                    </span>
                  );
                })()}
              </BreadcrumbItem>
            )}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );

  return content;
}
